import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthProvider';
import { RestApi } from '../../../services/rest-api';
import { useNavigate, useParams } from 'react-router';
import { PageContainer } from '../../../components/PageContainer';
import { Accordion, Alert, Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { IField } from '../../../interfaces/field.interface';
import { PurchaseOrderRow } from './purchase-order-row';
import { fetchPurchaseOrderShow, PurchaseOrderItemModel, PurchaseOrderModel } from '../../../model/purchase-order.model';
import { AccountMaster, BrandMaster, ColorMaster, fetchMasters, ItemMaster } from '../../../model/masters.model';
import { baseURL } from '../../../constants/config';
import { parseFixed } from '../../../services/formats';
import { useUtility } from '../../../contexts/UtilityContext';
import { Fields } from '../../../components/Fields';

export const PurchaseOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { authInfo } = useAuth();

  const params = useParams<{ id?: string }>();
  const { showLoader, hideLoader, toast } = useUtility();
  const [row, setRow] = useState<PurchaseOrderItemModel | undefined>();
  const [editIdx, setEditIdx] = useState<number | null>();
  const [accounts, setAccounts] = useState<Array<AccountMaster>>([]);
  const [items, setItems] = useState<Array<ItemMaster>>([]);
  const [brands, setBrands] = useState<Array<BrandMaster>>([]);
  const [colors, setColors] = useState<Array<ColorMaster>>([]);

  const fields: Array<IField> = [
    {
      label: "Order #",
      required: false,
      props: {
        name: "po_no",
        type: "text",
        inputMode: "text",
        placeholder: "Order #",
        readOnly: true,
      }
    }, {
      label: "Order Date",
      required: true,
      props: {
        name: "po_date",
        type: "date",
        inputMode: "text",
        placeholder: "Order Date"
      }
    }, {
      label: "Supplier",
      required: true,
      props: {
        name: "account",
        type: "typeahead",
        inputMode: "text",
        placeholder: "Supplier"
      },
      options: accounts,
    }, {
      label: "Delivery",
      required: false,
      props: {
        name: "delivery",
        type: "text",
        inputMode: "text",
        placeholder: "Delivery"
      }
    }, {
      label: "Remarks",
      required: false,
      props: {
        name: "remarks",
        type: "text",
        inputMode: "text",
        placeholder: "Remarks"
      }
    }
  ];

  const { register, control, handleSubmit, reset, setValue, watch } = useForm<PurchaseOrderModel>({
    defaultValues: {
      po_date: new Date().toISOString().split('T')[0],
      items: [],
    },
    mode: "onTouched",
    reValidateMode: "onChange"
  });

  const itemList = watch('items');
  const qty = watch('qty');
  const amt = watch('amt');

  useEffect(() => {
    async function fetchData() {
      try {
        showLoader();
        const masters = await fetchMasters('po', authInfo);
        setAccounts(masters?.accounts ?? []);
        setItems(masters?.items ?? []);
        setColors(masters?.colors ?? []);
        setBrands(masters?.brands ?? []);

        if (params.id) {
          const res = await fetchPurchaseOrderShow(params.id, authInfo);
          reset({
            ...res,
            account: (masters?.accounts ?? []).find(ac => ac.id === res.ac_id),
            items: res.items.map((itm: any) => ({
              ...itm,
              item: (masters?.items ?? []).find(i => i.id === itm.item_id)!,
              brand: (masters?.brands ?? []).find(b => b.id === itm.brand_id)!,
              color: (masters?.colors ?? []).find(c => c.id === itm.color_id)!,
            })),
          });
        }
      } catch (err) {
        console.log(err);
      } finally {
        hideLoader();
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, authInfo, reset]);

  useEffect(() => {
    const bagSum = itemList.reduce((acc, cur) => (acc + parseFixed(cur.bag)), 0);
    const qtySum = itemList.reduce((acc, cur) => (acc + parseFixed(cur.qty)), 0);
    const amtSum = itemList.reduce((acc, cur) => (acc + parseFixed(cur.amt)), 0);
    setValue('bag', bagSum);
    setValue('qty', qtySum);
    setValue('amt', amtSum);
  }, [itemList, setValue]);

  const [show, setShow] = useState(false);
  const handleClose = () => {
    setShow(false);
    setEditIdx(null);
    setRow(undefined);
  };
  const handleShow = () => setShow(true);

  const handleEditItem = (idx: number) => {
    setEditIdx(idx);
    setRow(itemList[idx]);
    handleShow();
  };

  const handleRemoveItem = (idx: number) => {
    const items = [...itemList];
    items.splice(idx, 1);
    setValue('items', items);
  };

  const handleItemRow = (item: PurchaseOrderItemModel) => {
    let items = [...itemList];
    if (editIdx !== null && editIdx !== undefined) {
      items = items.map((dataItem, idx) => idx === editIdx ? item : dataItem);
      setEditIdx(null);
    } else {
      items.push(item);
    }
    setValue('items', items);
  };

  const onSubmit = handleSubmit(async (inputs) => {
    try {
      showLoader();
      await RestApi('post', params?.id ? 'updatePurchaseOrder' : 'createPurchaseOrder', {
        ...inputs,
        ...(params?.id ? { id: params.id } : {}),
        ac_id: inputs.account?.id,
        items: inputs.items && inputs.items.map(item => ({
          item_id: item.item?.id,
          color_id: item.color?.id,
          brand_id: item.brand?.id,
          bag: item.bag,
          qty: item.qty,
          rate: item.rate,
          gst_per: item.gst_per,
          net_rate: item.net_rate,
          amt: item.amt,
        })),
      }, authInfo.user.authorization);
      navigate(`${baseURL}po-list`);
    } catch (message) {
      toast(message, 'danger');
    } finally {
      hideLoader();
    }
  });

  return (
    <PageContainer
      title="Purchase Order Form"
      body={<>
        <Form onSubmit={onSubmit} noValidate>
          <div>
            <Fields fields={fields} control={control} register={register} />
          </div>
          <Row className='mb-3'>
            <Col className='text-start'>
              <strong>Order Items</strong>
            </Col>
            <Col className='text-end'>
              <Button size='sm' variant="secondary" onClick={() => {
                setRow(undefined);
                handleShow();
              }}>Add Item</Button>
            </Col>
          </Row>
          {itemList.length === 0 && <Alert variant='info'>No items added.</Alert>}
          <Accordion>
            {!!itemList && itemList.map((itm, idx) => (
              <Accordion.Item key={`item-${idx}`} eventKey={`${idx}`}>
                <Accordion.Header>
                  <Container>
                    <Row>
                      <Col>{itm.item?.name}</Col>
                      <Col>Qty: {itm.qty}</Col>
                    </Row>
                  </Container>
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>Brand<br />{itm.brand?.name}</Col>
                    <Col>Color<br />{itm.color?.name}</Col>
                    <Col>Bags<br />{itm.bag}</Col>
                    <Col>Rate<br />{itm.rate}</Col>
                    <Col>GST<br />{itm.gst_per}</Col>
                    <Col>Rate<br />{itm.net_rate}</Col>
                    <Col>Amount<br />{itm.amt}</Col>
                  </Row>
                  <div className='d-flex justify-content-end gap-2 mt-3'>
                    <Button size='sm' variant="info" onClick={() => handleEditItem(idx)}>Edit</Button>
                    <Button size='sm' variant="danger" onClick={() => handleRemoveItem(idx)}>Remove</Button>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
          <Row>
            <Col className='text-start'>Total Qnty<br />{qty}</Col>
            <Col className='text-end'>Total Amount<br />{amt}</Col>
          </Row>
          <Button className='w-100 mt-3' variant='primary' type="submit">Submit</Button>
        </Form>
        {show && <PurchaseOrderRow
          brands={brands}
          colors={colors}
          items={items}
          defaultData={row}
          handleSubmit={handleItemRow}
          handleClose={handleClose}
          show={show}
        />
        }
      </>
      }
    />
  );
};